import React, { useEffect, useRef, useState } from 'react';
import { Check, ChevronDown, SlidersHorizontal } from 'lucide-react';

import { tx } from './i18n';

// The filter of the Store section's lists: one button that says what is shown
// and how many, and a menu to change it. The first option is everything.
export function FilterMenu({ value, onChange, options, label }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  const active = options.find((entry) => entry.value === value) ?? options[0];

  useEffect(() => {
    if (!open) return undefined;
    const closeOnPointer = (event) => { if (!ref.current?.contains(event.target)) setOpen(false); };
    const closeOnEscape = (event) => { if (event.key === 'Escape') setOpen(false); };
    document.addEventListener('mousedown', closeOnPointer);
    document.addEventListener('keydown', closeOnEscape);
    return () => {
      document.removeEventListener('mousedown', closeOnPointer);
      document.removeEventListener('keydown', closeOnEscape);
    };
  }, [open]);

  return (
    <div className="xo-product-filter-control" ref={ref}>
      <button type="button" className={`xo-product-filter-button${active.value !== options[0].value ? ' is-filtered' : ''}`}
        aria-haspopup="menu" aria-expanded={open} aria-label={`${label}: ${active.label}`} onClick={() => setOpen((was) => !was)}>
        <SlidersHorizontal aria-hidden="true" />
        <span>{active.label}</span><b>{active.count}</b>
        <ChevronDown aria-hidden="true" />
      </button>
      {open && (
        <div className="xo-product-filter-menu" role="menu">
          {options.map((entry) => (
            <button type="button" role="menuitemradio" key={entry.value} aria-checked={active.value === entry.value}
              className={active.value === entry.value ? 'is-active' : ''}
              onClick={() => { onChange(entry.value); setOpen(false); }}>
              <span>{entry.label}</span><b>{entry.count}</b>
              {active.value === entry.value && <Check aria-hidden="true" />}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

// The kinds a list of products is filtered by: kits, supplements, wearables,
// and everything else together.
const NAMED_KINDS = ['kit', 'supplement', 'wearable'];

export const isKind = (product, kind) => kind === 'all'
  || (kind === 'other' ? !NAMED_KINDS.includes(product.itemType) : product.itemType === kind);

// The kinds as options, each with how many of `products` it keeps.
export function kindOptions(products) {
  const count = (kind) => products.filter((product) => isKind(product, kind)).length;
  return [
    { value: 'all', label: tx('ui.products.filter_all'), count: products.length },
    { value: 'kit', label: tx('ui.products.filter_kits'), count: count('kit') },
    { value: 'supplement', label: tx('ui.products.filter_supplements'), count: count('supplement') },
    { value: 'wearable', label: tx('ui.products.filter_wearables'), count: count('wearable') },
    { value: 'other', label: tx('ui.products.filter_other'), count: count('other') },
  ];
}
